import {
    AttachedFiles,
    AttachedFilesRequest,
    type BTFileSystem,
    type IHasAttachedFiles,
} from "legacyComponents/FileUploadContainer.types";
import { LineItemType } from "legacyComponents/LineItemContainer.types";
import { partition } from "lodash-es";
import moment from "moment";
import { type RouteComponentProps } from "react-router";

import type { AutosaveLineItemResponse } from "@btnet/services-btnet/handlers/models/autosaveLineItemResponse";
import type { BaseLineItem } from "@btnet/services-btnet/handlers/models/baseLineItem";
import type { EntityCreateMapping } from "@btnet/services-btnet/handlers/models/entityCreateMapping";
import type { ExternalVendorProductLink } from "@btnet/services-btnet/handlers/models/externalVendorProductLink";
import type { LineItem } from "@btnet/services-btnet/handlers/models/lineItem";
import type { LineItemType as HandlerLineItemType } from "@btnet/services-btnet/handlers/models/lineItemType";
import { MarkupColumnTypes } from "@btnet/services-btnet/handlers/models/markupColumnTypes";
import type { ProposalDisplayColumnTypes } from "@btnet/services-btnet/handlers/models/proposalDisplayColumnTypes";
import type { RecategorizeProposalFormatRequestNewGroupType } from "@btnet/services-btnet/handlers/models/recategorizeProposalFormatRequestNewGroupType";
import type { RecategorizeType } from "@btnet/services-btnet/handlers/models/recategorizeType";
import type { RelatedItemResponse } from "@btnet/services-btnet/handlers/models/relatedItemResponse";

import { type BuilderInfo, type UserInfo } from "helpers/AppProvider.types";

import { type CostTypes, DocumentInstanceType, MarkedAs, ProposalGroupTypes } from "types/enum";

import type yup from "utilities/form/yup";
import { calculateMarginPercentageFromMarkup } from "utilities/lineItem/lineItemHelper";
import { isNullOrUndefined } from "utilities/object/object";

import { EntityRecordLinkInfo } from "commonComponents/entity/entityRecord/EntityRecord.types";
import { type ILineItemIdsAndTypes } from "commonComponents/entity/invoicing/LineItemsToInvoice/LineItemsToInvoice.api.types";
import { type IRelatedItemResponse } from "commonComponents/entity/relatedItem/RelatedItem.types";
import { type IFinancialImportDropdownProps } from "commonComponents/financial/FinancialImportDropdown/FinancialImportDropdown.types";
import { type IActionCellAction } from "commonComponents/utilities/LineItemContainer/cellImplementations/ActionCell/ActionCell";
import { type GridLineItemEntityType } from "commonComponents/utilities/LineItemContainer/types/LineItem.enums";
import {
    type ILineItemGroup,
    type ILineItemRow,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.interfaces";
import {
    type IMarkupLineItem,
    type ITaxLineItem,
} from "commonComponents/utilities/LineItemContainer/types/LineItem.types";

import { type IAdjustMarkupFormValues } from "entity/estimate/AdjustPercentMarkup/AdjustPercentMarkup.api.types";
import {
    type IJumpToQuickFindItem,
    type IQuickFindItem,
} from "entity/estimate/common/LineItemQuickFind/LineItemQuickFind.types";
import {
    type IProposalFormatData,
    type IProposalFormatFormValues,
} from "entity/estimate/common/ProposalFormat/ProposalFormat.types";
import { type IWorksheetRowEdit } from "entity/estimate/common/ProposalFormat/WorksheetAGGrid.utility";
import { type ProposalGroupDropdownProps } from "entity/estimate/common/ProposalGroupDropdown/ProposalGroupDropdown.types";
import {
    type EstimateActions,
    type EstimateDisplayPreferences,
    type EstimateResponse,
    type TabKeys,
} from "entity/estimate/Estimate/Estimate.api.types";
import type worksheetData from "entity/estimate/Estimate/Worksheet.api.json";
import { type IFilterDrawerProps } from "entity/filters/Filter/FilterDrawer.types";
import { type IJobHandler } from "entity/job/Job.api.handler";
import { type IPoLineItemIdTypeMapping } from "entity/purchaseOrder/PoMapping/PoMappingInternal/PoMappingInternal.api.types";
import { NoTaxId } from "entity/tax/TaxRate/TaxRate.api.types";
import { type IExternalProductLink, type VendorTypes } from "entity/vendor/common/vendor.types";

type CategoryData = (typeof worksheetData.formatData)[0] & { _isDirty?: boolean };

export interface IEstimateLineItem extends IMarkupLineItem, ITaxLineItem, ILineItemRow {
    costCategoryId: number | null;
    assemblyId: number | null;
    quantity: number;
}

export const NewEntityId = -2;

export enum OptionMode {
    Required = 0,
    Pending = 1,
    Approved = 2,
    Declined = 3,
}

export enum LineItemsToGet {
    All = 0,
    InScope = 1,
    OutOfScope = 2,
    OnlyRequired = 3,
    OnlyPending = 4,
    OnlyApproved = 5,
    OnlyDeclined = 6,
    OnlyFavorited = 7,
    OnlyDecided = 8,
}

export const InScopeOptionModes: readonly OptionMode[] = [OptionMode.Required, OptionMode.Approved];

export class ProposalCategory implements IHasAttachedFiles, ILineItemGroup {
    constructor(
        categoryData: any,
        builderId: number,
        jobId?: number,
        leadId?: number,
        readonlyLineItems: boolean = true,
    ) {
        // The worksheet json doesn't match this type exactly, but this can be used for properties that do match
        const typedCategoryData = categoryData as CategoryData;

        this.id = categoryData.id;
        this.costCategoryId = categoryData.costCategoryId;
        this.assemblyId = categoryData.assemblyId;
        this.title = categoryData.title;
        this.description = categoryData.description;
        this.displayOrder = categoryData.displayOrder;

        const unorderedLineItems: WorksheetLineItem[] = (
            (categoryData.lineItems ?? []) as any[]
        ).map((li) => {
            const lineItem = new WorksheetLineItem({ ...li, parentId: this.id });
            if (readonlyLineItems) {
                lineItem.isEditable = false;
            }
            return lineItem;
        });

        // currently change order line items are missing display order, leave them in the order received for consistency
        const [orderedChangeOrderLineItems, unorderedLineItemsWithDisplayOrder] = partition(
            unorderedLineItems,
            (li) => li.lineItemType === LineItemType.ChangeOrder,
        );

        const orderedLineItemsWithDisplayOrder = unorderedLineItemsWithDisplayOrder.sort(
            (subItem1, subItem2) => {
                if (isNullOrUndefined(subItem1.displayOrder)) {
                    return 1;
                }

                if (isNullOrUndefined(subItem2.displayOrder)) {
                    return -1;
                }

                return subItem1.displayOrder - subItem2.displayOrder;
            },
        );

        const orderedSubgroups: ProposalSubCategory[] = ((categoryData.subGroups ?? []) as any[])
            .map((subGroup) => {
                let lineItems = undefined;
                if (subGroup.estimates) {
                    lineItems = subGroup.estimates?.map((li: any) => new LeadProposalLineItem(li));
                } else if (subGroup.lineItems) {
                    lineItems = subGroup.lineItems?.map((li: any) => new WorksheetLineItem(li));
                }

                return new ProposalSubCategory(
                    { ...subGroup, lineItems },
                    builderId,
                    jobId,
                    leadId,
                    readonlyLineItems,
                );
            })
            .sort((subItem1, subItem2) => subItem1.displayOrder - subItem2.displayOrder);

        // ideally we rely entirely on display order here, but until change order line items have
        // pfis, we need to ensure they follow our current rules of line items before subgroups
        this.items = [
            ...orderedLineItemsWithDisplayOrder,
            ...orderedChangeOrderLineItems,
            ...orderedSubgroups,
        ];

        // IHasAttachedFiles
        if (categoryData.attachments || categoryData.attachedFiles) {
            // if uncategorized, this will be null
            this.attachedFiles = new AttachedFiles(
                categoryData.attachments || categoryData.attachedFiles,
            );
            this.attachments = new AttachedFilesRequest();
        }
        this.documentInstanceType = DocumentInstanceType.ProposalFormatItem;
        this.jobId = jobId;
        this.leadId = leadId;
        this.builderId = builderId;
        this.isExpanded = categoryData.isExpanded;
        this.isSelected = false;
        this.aggregates = null;
        this.value = categoryData.title;
        this.field = categoryData.id;
        this.openAddAttachFiles = false;
        this.openCreateDocument = false;
        this.mountFileComponent = (this.attachedFiles?.files.length ?? 0) > 0;
        this._isInEdit = categoryData._isInEdit ?? false;
        this._isDirty = typedCategoryData._isDirty ?? false;
        this.files = this.attachedFiles?.files;
        this.viewAllFiles = false;
        this.parentId = categoryData.parentId;
        this.optionMode = categoryData.optionMode ?? OptionMode.Required;

        // this shouldn't be relied upon going forward, we dynamically choose the name for attached files when needed
        this.name = "Category";
        this.externalId = typedCategoryData.externalId;
        this.entityRecordLinks = (typedCategoryData.entityRecordLinks ?? []).map(
            (link) => new EntityRecordLinkInfo(link),
        );
        this.mapping = typedCategoryData.mapping ?? undefined;
    }

    id: number;
    costCategoryId: number | null;
    assemblyId?: number;
    title: string;
    description: string | null;
    displayOrder: number;
    parentId?: number;
    optionMode: OptionMode;

    // IHasAttachedFiles
    attachedFiles: AttachedFiles; // used for setting up the file upload container
    attachments: AttachedFilesRequest; // used for adding files for new cost groups
    builderId: number;
    documentInstanceType: DocumentInstanceType;
    jobId: number | undefined;
    leadId: number | undefined;
    name: string;

    openAddAttachFiles: boolean;
    openCreateDocument: boolean;
    viewAllFiles: boolean;
    // value used to determine whether or not to add the file component to the page.
    // on proposals with many categories, the mass mounting of this component is very slow
    // so we will only add it to the page when it is needed (the category has files, or they click the category dropdown to add new files)
    mountFileComponent: boolean;

    isExpanded: boolean;
    aggregates: any;
    field: string;
    isSelected: boolean;
    items: (WorksheetLineItem | ProposalSubCategory)[];
    value: any;
    autoFocusedField?: string;
    _isInEdit?: boolean;
    _isDirty?: boolean;
    files: BTFileSystem[] | undefined; // This files field is for holding files as they update without triggering a re-load to allow for new and existing cost groups to show all attachments on preview tab
    externalId?: string;
    entityRecordLinks: EntityRecordLinkInfo[];
    mapping?: EntityCreateMapping;
}

export class ProposalSubCategory extends ProposalCategory {
    constructor(
        subCategoryData: any,
        builderId: number,
        jobId?: number,
        leadId?: number,
        readonlyLineItems: boolean = true,
    ) {
        super(subCategoryData, builderId, jobId, leadId, readonlyLineItems);

        this.items = subCategoryData.lineItems
            ? subCategoryData.lineItems.map((lineItem: any) => {
                  const subLineItem =
                      lineItem.lineItemType === LineItemType.ProposalLineItem
                          ? new LeadProposalLineItem(lineItem)
                          : new WorksheetLineItem({ ...lineItem, parentId: this.id });
                  if (readonlyLineItems) {
                      subLineItem.isEditable = false;
                  }
                  return subLineItem;
              })
            : [];

        this.parentId = subCategoryData.parentId;

        // this shouldn't be relied upon going forward, we dynamically choose the name for attached files when needed
        this.name = "Sub Category";
    }

    parentId: number;
    items: WorksheetLineItem[];
}

export function getDefaultProposalCategoryAttachedFiles(jobId: number) {
    return new AttachedFiles({
        title: "Attachments",
        viewingPermissions: {
            allAllowedExtensions: [],
            allowFullSizeImages: false,
            allowedDocumentExtensions: [],
            allowedPhotoExtensions: [],
            allowedVideoExtensions: [],
            blacklistedExtensions: [],
            browseExistingDocs: {
                canBrowsePhotos: false,
                canBrowseDocuments: false,
                canBrowseVideos: false,
            },
            canAddFiles: false,
            canUploadDocuments: false,
            canUploadPhotos: false,
            canUploadVideos: false,
            documentListType: 1,
            ignoreBlacklist: false,
            isFileReplaceMode: false,
            jobsiteId: jobId,
            leadId: null,
            maxFileSize: 0,
            maxFiles: 0,
            minFileSize: 0,
            videoDurationLimit: null,
            vidoeMaxSizeMB: 0,
        },
        newFileOptions: {
            canEdit: false,
            defaultValue: false,
            fileOptionKey: 0,
            loginType: 0,
            options: [
                {
                    title: "Show",
                    loginType: 0,
                    fileOptionKey: 0,
                    defaultValue: false,
                    canEdit: false,
                    visible: false,
                    options: [
                        {
                            title: "Builder",
                            loginType: 1,
                            fileOptionKey: 5,
                            defaultValue: false,
                            canEdit: false,
                            visible: false,
                            options: null,
                        },
                        {
                            title: "Owner",
                            loginType: 2,
                            fileOptionKey: 3,
                            defaultValue: true,
                            canEdit: false,
                            visible: false,
                            options: null,
                        },
                        {
                            title: "Subs",
                            loginType: 3,
                            fileOptionKey: 1,
                            defaultValue: false,
                            canEdit: false,
                            visible: false,
                            options: null,
                        },
                    ],
                },
                {
                    title: "Notify",
                    loginType: 0,
                    fileOptionKey: 0,
                    defaultValue: false,
                    canEdit: false,
                    visible: false,
                    options: [
                        {
                            title: "Builder",
                            loginType: 1,
                            fileOptionKey: 6,
                            defaultValue: false,
                            canEdit: false,
                            visible: false,
                            options: null,
                        },
                        {
                            title: "Owner",
                            loginType: 2,
                            fileOptionKey: 4,
                            defaultValue: false,
                            canEdit: false,
                            visible: false,
                            options: null,
                        },
                        {
                            title: "Subs",
                            loginType: 3,
                            fileOptionKey: 2,
                            defaultValue: false,
                            canEdit: false,
                            visible: false,
                            options: null,
                        },
                    ],
                },
            ],
            title: null,
            visible: false,
        },
        files: [],
    });
}

export class LeadProposalLineItem implements IEstimateLineItem, ILineItemRow {
    constructor(data: any, takeoffProjectLink?: any, takeoffProjectName?: any) {
        this.id = data.id;
        this.lineItemType = LineItemType.ProposalLineItem;
        this.builderId = data.builderId;
        this.leadId = data.leadId;
        this.proposalId = data.proposalId;
        // from the service, we pass down costCode, but client side we use costCodeId
        this.costCodeId = data.costCodeId ?? data.costCode ?? null;
        this.costCategoryId = data.costCategoryId;
        this.costItemId = data.costItemId;
        this.itemTitle = data.title ?? data.itemTitle;
        this.costTypes = data.costTypes ?? null;
        this.markedAs = data.markedAs;
        this.unitCost = data.unitCost;
        this.quantity = data.quantity;
        this.unit = data.unit;
        this.builderCost = data.builderCost;
        this.markupType = data.markupType;
        this.markupPercent = data.markupPercent;
        this.markupPerUnit = data.markupPerUnit;
        this.markupAmount = data.markupAmount;
        this.ownerPrice = data.ownerPrice;
        this.margin =
            data.margin ??
            calculateMarginPercentageFromMarkup(
                data.markupPercent ?? 0,
                data.markupAmount ?? 0,
                data.markupPerUnit ?? 0,
                data.unitCost ?? 0,
                data.quantity ?? 0,
            );
        this.description = data.description;
        this.internalNotes = data.internalNotes;
        this.displayOrder = data.displayOrder;
        this.dateAddedForSorting = data.dateAdded ? moment(data.dateAdded) : null;
        this.worksheetDisplayOrder = data.worksheetDisplayOrder ?? null;
        this._isInEdit = data._isInEdit ?? false;
        this.isSelected = data.isSelected ?? false;
        this.isEditable = data.isEditable ?? true;
        this.isFirstEdit = data.isFirstEdit ?? false;
        this.formatId = data.formatId;
        this.costCategoryFormatId = data.costCategoryFormatId;
        this.parentId = data.parentId;
        this.includeItemInCatalog = false;
        this.isQuantityLinked = data.isQuantityLinked;
        this.isUnitTypeLinked = data.isUnitTypeLinked;
        this.isMarkupLinked = data.isMarkupLinked;
        this.autoFocusedField = data.autoFocusedField;
        this.assemblyId = data.assemblyId;
        this.takeoffLineItemId = data.takeoffLineItemId;
        this.takeoffProjectLink = takeoffProjectLink;
        this.takeoffProjectName = takeoffProjectName;
        this.vendorProductId = data.vendorProductId;
        this.vendorType = data.vendorType as VendorTypes;
        this.isDiscontinued = data.isDiscontinued;
        this.productLink = data.productLink;
        this.taxGroupId = data.taxGroupId ?? NoTaxId;
        this.totalWithTax = data.totalWithTax;
    }
    id: number;
    lineItemType: LineItemType;
    builderId: number;
    leadId: number;
    proposalId: number;
    costCodeId: number | null;
    costCategoryId: number;
    costItemId: number | null;
    itemTitle: string | null;
    costTypes: CostTypes[] | null;
    markedAs: MarkedAs;
    unitCost: number;
    quantity: number;
    unit: string;
    builderCost: number;
    markupType: MarkupColumnTypes;
    markupPercent: number;
    markupPerUnit: number;
    markupAmount: number;
    ownerPrice: number;
    margin: number | undefined;
    description: string;
    internalNotes: string | null;
    displayOrder: number;
    dateAddedForSorting?: moment.Moment | null;
    worksheetDisplayOrder: number | null;

    // vendor product
    vendorProductId?: number;
    vendorType?: VendorTypes;
    isDiscontinued?: boolean;
    productLink?: IExternalProductLink;

    _isInEdit: boolean;
    isEditable: boolean;
    isExpanded: boolean;
    isSelected: boolean;
    isFirstEdit: boolean;

    formatId: number;
    costCategoryFormatId: number;
    parentId?: number;
    includeItemInCatalog: boolean;

    isQuantityLinked: boolean;
    isUnitTypeLinked: boolean;
    isMarkupLinked: boolean;
    autoFocusedField?: string;

    assemblyId: number | null;
    takeoffLineItemId?: number;
    takeoffProjectLink?: string;
    takeoffProjectName?: string;

    _isDirty?: boolean;
    taxGroupId?: number;
    totalWithTax: number;
}

interface IWorksheetLineItemFormatData {
    formatId?: number | null;
    costCategoryFormatId?: number | null;
    displayOrder?: number | null;
}

/**
 * WorksheetLineItem is built from several different sources (worksheet json, autosave
 * responses, drag-and-drop from search, etc.) whose shapes don't match this type exactly.
 * This describes the properties the constructor actually reads/relies on.
 */
export interface IWorksheetLineItemData {
    id?: number;
    builderId?: number | null;
    jobId?: number | null;
    lineItemType?: LineItemType;
    costCodeId?: number | null;
    costCodeTitle?: string | null;
    costCategoryId?: number | null;
    costItemId?: number | null;
    itemTitle?: string | null;
    title?: string | null;
    relatedItems?: IRelatedItemResponse[] | RelatedItemResponse[] | null;
    costTypes?: CostTypes[] | null;
    markedAs?: MarkedAs;
    unitCost?: number;
    unit?: string | null;
    quantity?: number | null;
    builderCost?: number;
    markupType?: MarkupColumnTypes;
    markupPercent?: number;
    markupPerUnit?: number;
    markupAmount?: number;
    ownerPrice?: number;
    margin?: number | null;
    amountInvoiced?: number | null;
    description?: string | null;
    internalNotes?: string | null;
    includeItemInCatalog?: boolean;
    hasRelatedPurchaseOrder?: boolean | null;
    relatedGeneralItemId?: number | null;
    relatedPurchaseOrderLineItemId?: number | null;
    _isInEdit?: boolean;
    isSelected?: boolean;
    isEditable?: boolean;
    dateAdded?: moment.MomentInput;
    parentId?: number | null;
    formatData?: IWorksheetLineItemFormatData;
    formatId?: number | null;
    costCategoryFormatId?: number | null;
    displayOrder?: number | null;
    proposalFormatItemId?: number;
    productId?: number | null;
    vendorType?: VendorTypes | string | number | null;
    discontinued?: boolean | null;
    productLink?: IExternalProductLink | ExternalVendorProductLink;
    assemblyId?: number | null;
    takeoffLineItemId?: number | null;
    taxGroupId?: number | null;
    totalWithTax?: number;
    selectionOptionGroupId?: number | null;
    retainageWithheld?: number | null;
}

export class WorksheetLineItem implements IEstimateLineItem, ILineItemRow {
    constructor(
        data: IWorksheetLineItemData,
        takeoffProjectLink?: string,
        takeoffProjectName?: string,
    ) {
        this.id = data.id ?? 0;
        this.builderId = data.builderId ?? 0;
        this.jobId = data.jobId ?? 0;
        this.lineItemType = data.lineItemType ?? LineItemType.EstimateLineItem;
        this.costCodeId = data.costCodeId ?? null;
        this.costCodeTitle = data.costCodeTitle ?? undefined;
        this.costCategoryId = data.costCategoryId ?? null;
        this.costItemId = data.costItemId ?? null;
        this.itemTitle = data.itemTitle ?? data.title ?? null;
        this.relatedItems =
            (data.relatedItems as IRelatedItemResponse[] | null | undefined) ?? null;
        this.costTypes = data.costTypes ?? null;
        this.markedAs = data.markedAs ?? MarkedAs.None;
        this.unitCost = data.unitCost ?? 0;
        this.unit = data.unit ?? "";
        this.quantity = data.quantity ?? 0;
        this.builderCost = data.builderCost ?? 0;
        this.markupType = data.markupType ?? MarkupColumnTypes.Percentage;
        this.markupPercent = data.markupPercent ?? 0;
        this.markupPerUnit = data.markupPerUnit ?? 0;
        this.markupAmount = data.markupAmount ?? 0;
        this.ownerPrice = data.ownerPrice ?? 0;
        this.margin =
            data.margin ??
            calculateMarginPercentageFromMarkup(
                data.markupPercent ?? 0,
                data.markupAmount ?? 0,
                data.markupPerUnit ?? 0,
                data.unitCost ?? 0,
                data.quantity ?? 0,
            );
        this.amountInvoiced = data.amountInvoiced ?? null;
        this.description = data.description ?? "";
        this.internalNotes = data.internalNotes ?? null;
        this.includeItemInCatalog = data.includeItemInCatalog ?? false;
        this.hasRelatedPurchaseOrder = data.hasRelatedPurchaseOrder ?? null;
        this.relatedGeneralItemId = data.relatedGeneralItemId ?? null;
        this.relatedPurchaseOrderLineItemId = data.relatedPurchaseOrderLineItemId ?? null;
        this._isInEdit = data._isInEdit ?? false;
        this.isSelected = data.isSelected ?? false;
        this.isEditable =
            data.isEditable ??
            [
                LineItemType.Bid,
                LineItemType.EstimateLineItem,
                LineItemType.ProposalLineItem,
                LineItemType.ChangeOrder,
            ].includes(this.lineItemType);
        this.dateAddedForSorting = data.dateAdded ? moment(data.dateAdded) : null;

        if (data.parentId) {
            this.parentId = data.parentId;
        }

        if (data.formatData) {
            this.formatId = data.formatData.formatId ?? null;
            this.costCategoryFormatId = data.formatData.costCategoryFormatId ?? null;
            this.displayOrder = data.formatData.displayOrder ?? null;
        } else {
            // if the formatData is blank, we might be able to get the direct values
            this.formatId = data.formatId ?? null;
            this.costCategoryFormatId = data.costCategoryFormatId ?? null;
            this.displayOrder = data.displayOrder ?? null;
        }
        this.importedFromProposalFormatItemId = data.proposalFormatItemId;

        this.vendorProductId = data.productId ?? undefined;
        this.vendorType = (data.vendorType as VendorTypes) ?? undefined;
        this.isDiscontinued = data.discontinued ?? undefined;
        this.productLink = data.productLink as IExternalProductLink | undefined;

        this.assemblyId = data.assemblyId ?? null;
        this.takeoffLineItemId = data.takeoffLineItemId ?? undefined;
        this.takeoffProjectLink = takeoffProjectLink;
        this.takeoffProjectName = takeoffProjectName;

        this.taxGroupId = data.taxGroupId ?? NoTaxId;
        this.totalWithTax = data.totalWithTax ?? 0;
        this.selectionOptionGroupId = data.selectionOptionGroupId ?? null;
        this.retainageWithheld = data.retainageWithheld ?? undefined;
    }

    static FromAutoSaveLineItem(request: LineItem, response: AutosaveLineItemResponse) {
        const newItem: WorksheetLineItem = {
            id: response.lineItemId!,
            builderId: request.builderId!,
            jobId: request.jobId!,

            itemTitle: request.title ?? "",
            description: request.description ?? "",

            unitCost: request.unitCost ?? 0,
            quantity: request.quantity ?? 0,
            builderCost: request.builderCost ?? 0,
            ownerPrice: request.ownerPrice ?? 0,
            markupAmount: request.markupAmount ?? 0,
            markupPercent: request.markupPercent ?? 0,
            markupType: request.markupType!,
            costCodeId: request.costCode!,
            costCodeTitle: request.costCodeTitle ?? undefined,
            markupPerUnit: request.markupPerUnit ?? 0,
            margin: request.margin ?? 0,
            costCategoryFormatId: null,
            dateAddedForSorting: null,
            displayOrder: 0,
            internalNotes: "",
            totalWithTax: request.totalWithTax ?? 0,
            taxGroupId: request.taxGroupId ?? NoTaxId,
            parentId: -1,
            costTypes: request.costTypes ?? null,
            lineItemType: request.pageTypeEnum ?? LineItemType.EstimateLineItem,

            formatId: null,
            hasRelatedPurchaseOrder: null,
            relatedGeneralItemId: null,
            relatedItems: null,
            relatedPurchaseOrderLineItemId: null,
            costCategoryId: null,
            costItemId: null,
            markedAs: MarkedAs.None,
            unit: "",
            amountInvoiced: 0,
            includeItemInCatalog: false,
            isEditable: true,
            assemblyId: null,
            selectionOptionGroupId: null,
        };

        return new WorksheetLineItem(newItem);
    }

    id: number;
    builderId: number;
    jobId: number;
    lineItemType: LineItemType;
    costCodeId: number | null;
    costCodeTitle?: string;
    costCategoryId: number | null;
    costItemId: number | null;
    itemTitle: string | null;
    costTypes: CostTypes[] | null;
    markedAs: MarkedAs;
    unitCost: number;
    unit: string;
    quantity: number;
    builderCost: number;
    markupType: MarkupColumnTypes;
    markupPercent: number;
    markupPerUnit: number;
    markupAmount: number;
    ownerPrice: number;
    margin: number | undefined;
    amountInvoiced: number | null;
    description: string;
    internalNotes: string | null;
    includeItemInCatalog: boolean;
    parentId?: number;
    dateAddedForSorting?: moment.Moment | null;
    taxGroupId?: number;
    totalWithTax: number;

    // vendor product
    vendorProductId?: number;
    vendorType?: VendorTypes;
    isDiscontinued?: boolean;
    productLink?: IExternalProductLink;

    // IGridLineItemViewRow
    _isInEdit?: boolean;
    isSelected?: boolean;
    isExpanded?: boolean;
    isEditable: boolean;
    autoFocusedField?: string;

    // format data
    formatId: number | null;
    costCategoryFormatId: number | null;
    displayOrder: number | null;
    importedFromProposalFormatItemId?: number;

    assemblyId: number | null;
    takeoffLineItemId?: number;
    takeoffProjectLink?: string;
    takeoffProjectName?: string;

    // Related Items
    hasRelatedPurchaseOrder: boolean | null;
    relatedGeneralItemId: number | null;
    relatedItems: IRelatedItemResponse[] | null;
    relatedPurchaseOrderLineItemId: number | null;
    selectionOptionGroupId: number | null;
    retainageWithheld?: number;

    _isDirty?: boolean;
    _clonedFromId?: number;
}

interface ITakeoffPartner {
    id: number;
    name: string;
}

export class TakeoffPartner {
    constructor(data: ITakeoffPartner) {
        this.id = data.id;
        this.name = data.name;
    }

    id: number;
    name: string;
}

export class DisplayPreferences<ColumnType> {
    constructor(data: any) {
        this.estimateGroupType = data.estimateGroupType;
        this.estimateFormatGroupType = data.estimateFormatGroupType ?? ProposalGroupTypes.Assembly;
        this.columnDisplayPreferences = data.columnDisplayPreferences;
        this.columnPreferences = data.columnPreferences;
        this.consolidationStatus = data.consolidationStatus;
    }
    /**
     * The estimate "view"
     */
    estimateGroupType: ProposalGroupTypes;
    /**
     * If on Proposal Worksheet View, the estimate "grouping"
     */
    estimateFormatGroupType: ProposalGroupTypes;
    columnDisplayPreferences: ColumnType[];
    columnPreferences: IColumnModel[];
    consolidationStatus?: boolean;
}

export interface IFormatItemPathIndices {
    id: number;
    rootCategoryIndex: number;
    subCategoryIndex?: number;
    lineItemIndex?: number;
}

export type FormatItem = WorksheetLineItem | ProposalCategory;

export type FormatItemList =
    | WorksheetLineItem[] // None grouping format
    | ProposalCategory[] // Nested Data format
    | (ProposalCategory | WorksheetLineItem)[]; // Old flat mapped pattern

export type WorksheetValue = string | number | FormatItem | ILineItemRow | ProposalCategory[];

export const convertToLineItem = (lineItem: WorksheetLineItem): LineItem => {
    return {
        id: lineItem.id,
        costCode: lineItem.costCodeId ?? undefined,
        costCodeItemId: lineItem.costItemId ?? undefined,
        unitCost: lineItem.unitCost,
        quantity: lineItem.quantity,
        unitType: lineItem.unit,
        markupType: lineItem.markupType,
        markupPercent: lineItem.markupPercent,
        markupPerUnit: lineItem.markupPerUnit,
        markupAmount: lineItem.markupAmount,
        margin: lineItem.margin,
        ownerPrice: lineItem.ownerPrice,
        description: lineItem.description,
        internalNotes: lineItem.internalNotes,
        title: lineItem.itemTitle,
        relatedGeneralItemId: lineItem.relatedGeneralItemId,
        builderCost: lineItem.builderCost,
        pageTypeEnum: lineItem.lineItemType as HandlerLineItemType,
        parentId: lineItem.parentId,
        lineItemDisplayOrder: lineItem.displayOrder,
        costCodeTitle: lineItem.costCodeTitle,
        amountInvoiced: lineItem.amountInvoiced ?? undefined,
        costTypes: lineItem.costTypes,
        markedAs: lineItem.markedAs,
        includeInCatalog: lineItem.includeItemInCatalog,
        costCategoryId: lineItem.costCategoryId,
        vendorProductId: lineItem.vendorProductId,
        vendorType: lineItem.vendorType,
        productUrl: lineItem.productLink?.value,
        isDiscontinued: lineItem.isDiscontinued,
        taxGroupId: lineItem.taxGroupId,
        totalWithTax: lineItem.totalWithTax,
        builderId: lineItem.builderId,
        jobId: lineItem.jobId,
        assemblyId: lineItem.assemblyId,
        importedFromProposalFormatItemId: lineItem.importedFromProposalFormatItemId,
        takeoffLineItemId: lineItem.takeoffLineItemId,
    };
};

export const isProposalCategory = (formatItem: FormatItem): formatItem is ProposalCategory => {
    return (formatItem as ProposalCategory).items !== undefined;
};

export const isFormatItemListGrouped = (
    formatItems: FormatItemList,
): formatItems is ProposalCategory[] => {
    return formatItems.length > 0 && isProposalCategory(formatItems[0]);
};

export const UnassignedCostGroupId = -1;

export const isUnassignedCategory = (formatItem: FormatItem) => {
    return formatItem.id === UnassignedCostGroupId;
};

export interface IColumnModel {
    id: string;
    width: number;
}

export type EstimateGridData = (IEstimateLineItem | ILineItemGroup<IEstimateLineItem>)[];

export interface IEntityLineItemPermissions {
    canAdd: boolean;
    canDelete: boolean;
    canUpdate: boolean;
    /** Permission for whether line item actions involving the entity are available. */
    canUpdateEntity: boolean;
}

export interface IWorksheetActionRowConfig {
    rowText: string;
    onClick: () => void;
    position: "top" | "bottom";
}

/**
 * Selection metadata sent to `onApplyPercentMarkup` so the worksheet's parent
 * handler can report "X of Y items" in its success toast.
 */
export interface IApplyPercentMarkupSelectionContext {
    /** Count of items the user explicitly checked, or the full worksheet count when nothing was checked. */
    totalConsideredCount: number;
}

export interface IWorksheetPresentationalProps extends RouteComponentProps {
    jobId: number;
    estimateData: EstimateResponse;
    actionBeingPerformed: EstimateActions;
    lineItemToJumpTo?: IJumpToQuickFindItem;
    filterDrawerProps?: IFilterDrawerProps;
    externalImportProps?: IFinancialImportDropdownProps;
    overrideEmptyState?: React.ReactNode;
    onConvertLineItemToAllowance: (lineItem: WorksheetLineItem) => Promise<void>;
    onConvertLineItemsToAllowance: (lineItems: WorksheetLineItem[]) => Promise<void>;
    onDeleteLineItems?: (
        lineItemIds: number[],
        partialDelete?: boolean,
        isAllowance?: boolean,
    ) => Promise<void>;
    onOpenNewEntity: (
        lineItems: WorksheetLineItem[],
        includesAlreadyLinkedItems: boolean,
        entityType: GridLineItemEntityType,
    ) => void;
    onUpdateDisplayPreferences: (
        displayPreferences: EstimateDisplayPreferences,
        showSuccessOrErrorMessage: boolean,
    ) => Promise<void>;
    onApplyPercentMarkup?: (
        lineItems: BaseLineItem[],
        values: IAdjustMarkupFormValues,
        selectionContext: IApplyPercentMarkupSelectionContext,
    ) => Promise<void>;
    onBulkUpdateTaxable?: (lineItems: WorksheetLineItem[], isTaxable: boolean) => Promise<void>;
    onCreateInvoiceClick: (lineItemIdsAndTypes: ILineItemIdsAndTypes[]) => void;
    onAddToExistingInvoiceClick: (lineItemIds: number[]) => void;
    onInvoiceFromLineItemActionClick: (lineItem: WorksheetLineItem, isCreate: boolean) => void;
    onCreatePurchaseOrderClick: (lineItemIdsAndTypes: IPoLineItemIdTypeMapping[]) => void;
    onAddToExistingPurchaseOrderClick: (lineItemIdsAndTypes: IPoLineItemIdTypeMapping[]) => void;
    onAddFromCatalogClick?: () => void;
    onAddFromCostCodesClick?: () => void;
    onGenerateSpecsClick?: () => void;
    generateSpecsDisabled?: boolean;
    onSendVendorProductsToCart: (
        vendorType: VendorTypes,
        actionBeingPerformed: EstimateActions,
        lineItems: WorksheetLineItem[],
    ) => void;
    builderInfo?: BuilderInfo | null;
    userInfo: UserInfo;
    /** Allows parent components to pass in their entity specific line item permissions. */
    entityLineItemPermissions: IEntityLineItemPermissions;
    onModifyAttachments: () => Promise<void>;
    onDeleteFormatGroups: (
        formatGroupIds: number[],
        subgroupParentId: number | undefined,
    ) => Promise<void>;
    renderSharedTabContent: (
        expandParentGroups: (lineItem: IQuickFindItem) => void,
        leftSideActions?: React.ReactNode,
        checkedActions?: React.ReactNode,
    ) => React.ReactNode;
    onReplaceLineItemClick: (lineItemToReplace: WorksheetLineItem) => void;
    onCostItemAddedFromSearch?: (costItem: WorksheetLineItem) => Promise<void>;
    onReformatOrSort?: (
        recategorizeType: RecategorizeType,
        newGroupType?: RecategorizeProposalFormatRequestNewGroupType,
    ) => Promise<void>;
    onDuplicateLineItemClick: (lineItemToDuplicate: WorksheetLineItem) => void;
    onSaveFormatCategory?: (category: ProposalCategory) => Promise<ProposalCategory[]>;
    /**
     * Persists edits to an existing group/sub-group (id > 0). When provided, it handles saving a
     * group's content instead of routing the edit through onUpdateProposalFormatItems. Change orders
     * use this to persist title/description via the scope-of-work group endpoint; estimates omit it
     * and keep updating groups through onUpdateProposalFormatItems.
     */
    onEditFormatCategory?: (category: ProposalCategory) => Promise<ProposalCategory[]>;
    /**
     * Updates the proposal format items after editing a category, editing a subgroup, or moving a format item.
     * Returns: Full list of updated ProposalCategories
     */
    onUpdateProposalFormatItems: (categories: ProposalCategory[]) => Promise<ProposalCategory[]>;
    onMoveSelectedWorksheetItems: (
        destinationCategoryId: number,
        selectedLineItemId?: number,
    ) => void;
    onLineItemEditFinished?: (lineItem: WorksheetLineItem) => Promise<WorksheetLineItem | null>;
    onPutRowInEditMode: (fieldPath: string, newEditableRow: FormatItem) => void;
    onViewLineItemDetails?: (row: WorksheetLineItem) => void;
    /**
     * Container-query booleans, measured by the caller against the LIC's effective container.
     * Drives small-screen layout choices inside the worksheet so the LIC reacts to its
     * allotted space (e.g. inline panels) rather than the viewport.
     */
    isXSScreen: boolean;
    isSmallScreen: boolean;
    isMediumScreen: boolean;
    isLargeScreen: boolean;
    /** Attach this ref to the LIC's effective container so the booleans above track it. */
    sizerRef: (node: HTMLDivElement | null) => void;
    isRevisedCosts?: boolean;
    formatDataWithoutFiltering: ProposalCategory[];
    onTabChange: (activeTab: TabKeys) => Promise<void>;
    onCompleteLeadProposalImport?: () => Promise<void>;
    decidedOptionItems: Record<number, OptionMode>;
    /** Replaces Formik Props */
    setFieldValue: (field: string, value: WorksheetValue) => void;
    setValues: (values: IProposalFormatFormValues) => void;
    values: IProposalFormatFormValues;
    initialValues: IProposalFormatFormValues;
    isProposalWorksheet: boolean;
    jobHandler?: IJobHandler;
    hideAddGroupRow?: boolean;
    actionRowConfig?: IWorksheetActionRowConfig;
    isTemplate: boolean;
    /* Worksheet Sidebar */
    onAddLineItem?: () => void;
    onAddAllowance?: () => void;
    showColumnSettings?: boolean;
    allowedViews?: ProposalGroupTypes[];
    proposalFormatGroupDropdownProps?: ProposalGroupDropdownProps<string | undefined>;
    onAddNewGroup?: () => void;
    onAddSubGroup?: (parent: ProposalCategory) => void;
    /**
     * Entity-specific override for the group/sub-group row's "+ Item" action. See
     * WorksheetLineItemContainer's onAddLineItemToGroup prop for details. Also used by the AG
     * Grid worksheet to add a line item scoped to the given group/subgroup.
     */
    onAddLineItemToGroup?: (parent: ProposalCategory) => void;
    onMoveItemsClick?: () => void;
    /**
     * Hides the Move Items and Delete icons from the side toolbar, since they're
     * surfaced through the floating action bar instead. Estimate worksheets only -
     * Change Orders keep them in the toolbar (ado-281856).
     */
    hideMoveAndDeleteToolbarActions?: boolean;
    dataFieldOverride?: string;
    /**
     * Offset in px to adjust header and footer height in line item container height calculations, avoids double scrollbar.
     */
    headerFooterOffset?: number;
    /**
     * Determines if mass (checked) actions are disabled. Used for page state control, not permissions control.
     */
    disableLineItemMassActions?: boolean;
    /**
     * Determines if mass (checked) actions should be hidden entirely. Used for page state control, not permissions control.
     */
    hideLineItemMassActions?: boolean;
    disabledActionMessage?: string;
    canSetTaxRate: boolean;
    /**
     * Needed for change orders, where if the change order is in a sent state, only part of the fields are editable.
     */
    canFieldBeEditedInSentState: (field: ProposalDisplayColumnTypes) => boolean;
    clientPriceLocked: boolean;
    /**
     * Refetches the worksheet data. Used by the specs drawer to get fresh mapping
     * data that is built from line items at load time.
     */
    onReloadWorksheet: () => Promise<void>;
    baseProposalLICData: IProposalFormatData;
    lineItemActions: ((lineItem: WorksheetLineItem) => IActionCellAction)[];
    onGroupRowClick?: (category: ProposalCategory) => void;
    /**
     * Persists a worksheet row's batched inline edits (see {@link IWorksheetRowEdit}). Rows with a
     * negative id are unsaved, so the handler creates rather than updates.
     */
    onWorksheetRowEdited?: (edit: IWorksheetRowEdit) => void;
    /** Drops an abandoned unsaved row (negative id) from the worksheet data. */
    onWorksheetNewRowDiscarded?: (rowId: number) => void;
    /** Grid row id of a freshly-inserted unsaved row, whose title the grid opens for editing. */
    worksheetRowIdToEdit?: string;
    groupDetails?: ProposalCategory | null;
    onCloseGroupDetails?: () => void;
    isAGGridActive: boolean;
}

/**
 * Marker value used to identify a line item as a pending allowance.
 */
export const PENDING_ALLOWANCE_MARKER = -1;

export function setLineItemAsPendingAllowance(lineItem: WorksheetLineItem): void {
    lineItem.selectionOptionGroupId = PENDING_ALLOWANCE_MARKER;
}

export function isLineItemPendingAllowance(lineItem: WorksheetLineItem): boolean {
    return lineItem.selectionOptionGroupId === PENDING_ALLOWANCE_MARKER;
}

export function clearLineItemAllowanceMarker(lineItem: WorksheetLineItem): void {
    lineItem.selectionOptionGroupId = null;
}

/**
 * Checks if a line item has an associated selection option group (i.e., is a new allowance).
 */
export function hasSelectionOptionGroup(lineItem: WorksheetLineItem): boolean {
    return (
        lineItem.selectionOptionGroupId !== null &&
        lineItem.selectionOptionGroupId !== undefined &&
        lineItem.selectionOptionGroupId !== PENDING_ALLOWANCE_MARKER
    );
}

/**
 * Checks if a line item is a new allowance (either pending or saved).
 * New allowances live on estimates and change orders, so both line item types qualify.
 */
export function isNewAllowanceLineItem(lineItem: WorksheetLineItem): boolean {
    return (
        (lineItem.lineItemType === LineItemType.EstimateLineItem ||
            lineItem.lineItemType === LineItemType.ChangeOrder) &&
        (isLineItemPendingAllowance(lineItem) || hasSelectionOptionGroup(lineItem))
    );
}

/**
 * Checks if a line item is any type of allowance (legacy or new).
 */
export function isAnyAllowanceLineItem(lineItem: WorksheetLineItem): boolean {
    return lineItem.lineItemType === LineItemType.Allowance || isNewAllowanceLineItem(lineItem);
}

/**
 * Helper function to determine if a line item is an allowance based on lineItemType and selectionOptionGroupId
 */
function isAllowanceLineItem(
    lineItemType: LineItemType,
    selectionOptionGroupId: number | null | undefined,
): boolean {
    if (lineItemType === LineItemType.Allowance) {
        return true;
    }

    // New allowances: EstimateLineItem or ChangeOrder with selectionOptionGroupId set
    if (
        lineItemType === LineItemType.EstimateLineItem ||
        lineItemType === LineItemType.ChangeOrder
    ) {
        return (
            selectionOptionGroupId === PENDING_ALLOWANCE_MARKER ||
            (selectionOptionGroupId !== null &&
                selectionOptionGroupId !== undefined &&
                typeof selectionOptionGroupId === "number" &&
                selectionOptionGroupId > 0)
        );
    }

    return false;
}

/**
 * Creates a validation schema for itemTitle that requires a title for allowances.
 * This handles both legacy allowances and new allowances
 */
export function getItemTitleValidationForAllowances<T extends yup.StringSchema>(
    lineItemType: LineItemType | undefined,
    baseItemTitleSchema: T,
): T {
    // Use conditional validation based on both lineItemType and selectionOptionGroupId
    return baseItemTitleSchema.when(["lineItemType", "selectionOptionGroupId"], {
        is: isAllowanceLineItem,
        then: (schema: T) => schema.required().label("Allowance title"),
        otherwise: (schema: T) => schema,
    });
}

/**
 * Gets validation for item title in the line item details modal
 * - Allowances: Always require a title and show "Allowance Title" label
 * - Non-allowances: Title required only when includeItemInCatalog is checked (handled by base schema)
 */
export function getItemTitleValidationForLineItemDetails<T extends yup.StringSchema>(
    baseItemTitleSchema: T,
): T {
    return baseItemTitleSchema.when(["lineItemType", "selectionOptionGroupId"], {
        is: isAllowanceLineItem,
        then: (schema: T) => schema.required().label("Allowance Title"),
        otherwise: (schema: T) => schema,
    });
}
